/// <amd-module name="Repos/Views/Import/RestClient/Git" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\code\vss-code-web\repos\views\import\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Git from "Repos/Common/Contracts/Git";

export interface IGitRestClient extends IVssRestClient {
    /**
     * Validates a remote repository. Returns 404 if remote repository is not reachable
     * 
     * @param remoteRepository - The remote repository to validate.
     * @param project - Project ID or project name
     */
    validateRemoteRepository(remoteRepository: Git.ImportRepositoryValidation, project: string): Promise<Git.ImportRepositoryValidation>;
    /**
     * Create an import request.
     * 
     * @param importRequest - The import request to create.
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     */
    createImportRequest(importRequest: Git.GitImportRequest, project: string, repositoryId: string): Promise<Git.GitImportRequest>;
    /**
     * Retrieve a particular import request.
     * 
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     * @param importRequestId - The unique identifier for the import request.
     */
    getImportRequest(project: string, repositoryId: string, importRequestId: number): Promise<Git.GitImportRequest>;
    /**
     * Retry or abandon a failed import request.
     * 
     * @param importRequestToUpdate - The updated version of the import request. Currently, the only change allowed is setting the Status to Queued or Abandoned.
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     * @param importRequestId - The unique identifier for the import request to update.
     */
    updateImportRequest(importRequestToUpdate: Git.GitImportRequest, project: string, repositoryId: string, importRequestId: number): Promise<Git.GitImportRequest>;
    /**
     * Push changes to the repository.
     * 
     * @param push - 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    createPush(push: Git.GitPush, repositoryId: string, project?: string): Promise<Git.GitPush>;
    /**
     * Create a git repository in a team project.
     * 
     * @param gitRepositoryToCreate - Specify the repo name, team project and/or parent repository. Team project information can be omitted from gitRepositoryToCreate if the request is project-scoped (i.e., includes project Id).
     * @param project - Project ID or project name
     * @param sourceRef - [optional] Specify the source refs to use while creating a fork repo
     */
    createRepository(gitRepositoryToCreate: Git.GitRepositoryCreateOptions, project?: string, sourceRef?: string): Promise<Git.GitRepository>;
    /**
     * Retrieve all available templates of specified 'type'. If not specified, entire list is returned
     * 
     * @param project - Project ID or project name
     * @param type - searches for templates with this type
     */
    getTemplateList(project: string, type?: string): Promise<Git.GitTemplate[]>;
}

class GitRestClient extends RestClientBase implements IGitRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Validates a remote repository. Returns 404 if remote repository is not reachable
     * 
     * @param remoteRepository - The remote repository to validate.
     * @param project - Project ID or project name
     */
    public async validateRemoteRepository(
        remoteRepository: Git.ImportRepositoryValidation,
        project: string
        ): Promise<Git.ImportRepositoryValidation> {

        return this.beginRequest<Git.ImportRepositoryValidation>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/import/ImportRepositoryValidations",
            routeValues: {
                project: project
            },
            body: remoteRepository
        });
    }

    /**
     * Create an import request.
     * 
     * @param importRequest - The import request to create.
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     */
    public async createImportRequest(
        importRequest: Git.GitImportRequest,
        project: string,
        repositoryId: string
        ): Promise<Git.GitImportRequest> {

        return this.beginRequest<Git.GitImportRequest>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/importRequests/{importRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            body: importRequest
        });
    }

    /**
     * Retrieve a particular import request.
     * 
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     * @param importRequestId - The unique identifier for the import request.
     */
    public async getImportRequest(
        project: string,
        repositoryId: string,
        importRequestId: number
        ): Promise<Git.GitImportRequest> {

        return this.beginRequest<Git.GitImportRequest>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/importRequests/{importRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                importRequestId: importRequestId
            }
        });
    }

    /**
     * Retry or abandon a failed import request.
     * 
     * @param importRequestToUpdate - The updated version of the import request. Currently, the only change allowed is setting the Status to Queued or Abandoned.
     * @param project - Project ID or project name
     * @param repositoryId - The name or ID of the repository.
     * @param importRequestId - The unique identifier for the import request to update.
     */
    public async updateImportRequest(
        importRequestToUpdate: Git.GitImportRequest,
        project: string,
        repositoryId: string,
        importRequestId: number
        ): Promise<Git.GitImportRequest> {

        return this.beginRequest<Git.GitImportRequest>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/importRequests/{importRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                importRequestId: importRequestId
            },
            body: importRequestToUpdate
        });
    }

    /**
     * Push changes to the repository.
     * 
     * @param push - 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    public async createPush(
        push: Git.GitPush,
        repositoryId: string,
        project?: string
        ): Promise<Git.GitPush> {

        return this.beginRequest<Git.GitPush>({
            apiVersion: "5.0-preview.2",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pushes/{pushId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            body: push
        });
    }

    /**
     * Create a git repository in a team project.
     * 
     * @param gitRepositoryToCreate - Specify the repo name, team project and/or parent repository. Team project information can be omitted from gitRepositoryToCreate if the request is project-scoped (i.e., includes project Id).
     * @param project - Project ID or project name
     * @param sourceRef - [optional] Specify the source refs to use while creating a fork repo
     */
    public async createRepository(
        gitRepositoryToCreate: Git.GitRepositoryCreateOptions,
        project?: string,
        sourceRef?: string
        ): Promise<Git.GitRepository> {

        const queryValues: any = {
            sourceRef: sourceRef
        };

        return this.beginRequest<Git.GitRepository>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/Repositories/{repositoryId}",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            body: gitRepositoryToCreate
        });
    }

    /**
     * Retrieve all available templates of specified 'type'. If not specified, entire list is returned
     * 
     * @param project - Project ID or project name
     * @param type - searches for templates with this type
     */
    public async getTemplateList(
        project: string,
        type?: string
        ): Promise<Git.GitTemplate[]> {

        const queryValues: any = {
            type: type
        };

        return this.beginRequest<Git.GitTemplate[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/templates",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

}

export const GitClientName = "Repos.Import.Git.IGitRestClient";

export function getGitClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IGitRestClient {
    return pageContext.getRestClient<IGitRestClient>(GitClientName, options);
}

RestClients.add(GitClientName, { 
    factory: GitRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
